import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../mawi-ui';
import { useEditorStore } from '../../stores/editorStore';
import { createFile, getProjectFiles } from '../../services/editorApi';
import { toast } from 'sonner';

interface NewFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  isFolder?: boolean;
}

const languageOptions = [
  { value: 'plaintext', label: 'Plain Text' },
  { value: 'csharp', label: 'C#' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'typescriptreact', label: 'TypeScript React (TSX)' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'javascriptreact', label: 'JavaScript React (JSX)' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
  { value: 'yaml', label: 'YAML' },
  { value: 'markdown', label: 'Markdown' },
];

export function NewFileModal({ isOpen, onClose, isFolder = false }: NewFileModalProps) {
  const { currentProject, openFile, setFiles } = useEditorStore();
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('plaintext');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(`${isFolder ? 'Folder' : 'File'} name is required`);
      return;
    }

    if (!currentProject) {
      toast.error('No project selected');
      return;
    }

    setIsSubmitting(true);
    try {
      const fileData = {
        name: name.trim(),
        path: `/${name.trim()}`,
        content: '',
        language: isFolder ? undefined : language,
        file_type: isFolder ? 'folder' as const : 'file' as const,
      };

      const file = await createFile(currentProject.id, fileData);
      
      // Refresh files list
      const files = await getProjectFiles(currentProject.id);
      setFiles(files.map(f => ({
        id: f.id,
        name: f.name,
        path: f.path,
        content: f.content || '',
        language: f.language || 'plaintext',
        isDirty: false,
        projectId: f.project_id,
        file_type: f.file_type,
      })));

      // Open the new file if it's a file (not a folder)
      if (!isFolder) {
        openFile({
          id: file.id,
          name: file.name,
          path: file.path,
          content: file.content || '',
          language: file.language || 'plaintext',
          isDirty: false,
          projectId: file.project_id,
          file_type: file.file_type,
        });
      }

      toast.success(`${isFolder ? 'Folder' : 'File'} "${name}" created successfully!`);
      handleClose();
    } catch (error) {
      console.error('Failed to create file:', error);
      toast.error(`Failed to create ${isFolder ? 'folder' : 'file'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setLanguage('plaintext');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold">
            Create New {isFolder ? 'Folder' : 'File'}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {isFolder ? 'Folder' : 'File'} Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isFolder ? 'src' : 'index.tsx'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          {!isFolder && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Language
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : `Create ${isFolder ? 'Folder' : 'File'}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}